import React, { useState, useEffect, useRef } from 'react';
import { View, Text, StyleSheet, ScrollView, Dimensions, NativeScrollEvent, NativeSyntheticEvent, FlatList, Platform } from 'react-native';
import { useTheme } from '@emotion/react';
import getScreenType from '../../../utils/getScreenType';
import PlanCard, { PlanProps } from '../../atoms/ODSPlanCard';
import { ODSText } from '../../atoms/ODSText';

export type ODSPlanSelectorProps = {
  title?: string;
  plans: Omit<PlanProps, 'onSelect'>[];
  selectedPlanId?: string;
  onPlanSelect: (planId: string) => void;
  isLoading?: boolean;
  currentPlan?: string;
  onRegistration?: boolean;
};

const ODSPlanSelector = ({
  title = 'Choose your plan',
  plans,
  selectedPlanId,
  onPlanSelect,
  isLoading = false,
  currentPlan,
  onRegistration
}: ODSPlanSelectorProps) => {
  const { colors } = useTheme();
  const layout = getScreenType();
  const [selected, setSelected] = useState<string | undefined>(selectedPlanId);
  const flatListRef = useRef<FlatList>(null);
  const scrollViewRef = useRef<ScrollView>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const windowWidth = Dimensions.get('window').width;
  const isIOS = Platform.OS === 'ios';

  // For mobile, we want to show one full card with parts of adjacent cards
  const ITEM_WIDTH = layout === 'phone' ? windowWidth * 0.8 : 300;
  const ITEM_SPACING = 20;

  // Calculate the center offset properly
  const ITEM_OFFSET = ITEM_WIDTH + ITEM_SPACING;
  const FIRST_ITEM_INSET = (windowWidth - ITEM_WIDTH) / 2;

  // Find initial scroll index
  const initialIndex = plans.findIndex(plan => plan.id === selectedPlanId);

  // Set initial active index based on selected plan
  useEffect(() => {
    if (initialIndex > -1) {
      setActiveIndex(initialIndex);
    }
  }, []);

  // Auto-scroll to the selected plan when component mounts or selectedPlanId changes
  useEffect(() => {
    if (selectedPlanId && layout === 'phone' && flatListRef.current) {
      const selectedIndex = plans.findIndex(plan => plan.id === selectedPlanId);
      if (selectedIndex !== -1) {
        // Use a timeout to ensure the FlatList has rendered
        setTimeout(() => {
          try {
            flatListRef.current?.scrollToOffset({
              offset: selectedIndex * ITEM_OFFSET,
              animated: false
            });
            setActiveIndex(selectedIndex);
          } catch (error) {
            console.log('Error scrolling to index:', error);
          }
        }, 300);
      }
    }
  }, [selectedPlanId, plans, layout]);

  useEffect(() => {
    setSelected(selectedPlanId);
  }, [selectedPlanId]);

  const handleSelect = (id: string) => {
    setSelected(id);
    onPlanSelect(id);

    // Scroll to the selected plan
    const index = plans.findIndex(plan => plan.id === id);
    if (index !== -1 && layout === 'phone' && flatListRef.current) {
      try {
        flatListRef.current.scrollToOffset({
          offset: index * ITEM_OFFSET,
          animated: true
        });
        setActiveIndex(index);
      } catch (error) {
        console.log('Error scrolling to index:', error);
      }
    }
  };

  const handleScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    if (layout === 'phone') {
      const contentOffsetX = event.nativeEvent.contentOffset.x;
      const newIndex = Math.round(contentOffsetX / ITEM_OFFSET);

      if (newIndex !== activeIndex && newIndex >= 0 && newIndex < plans.length) {
        setActiveIndex(newIndex);
        // Update selected plan when scrolling
        if (plans[newIndex]) {
          setSelected(plans[newIndex].id);
        }
      }
    }
  };

  const onMomentumScrollEnd = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    if (layout === 'phone') {
      const contentOffsetX = event.nativeEvent.contentOffset.x;
      const newIndex = Math.round(contentOffsetX / ITEM_OFFSET);

      if (newIndex >= 0 && newIndex < plans.length && plans[newIndex]) {
        // Update selected plan when scrolling stops
        setSelected(plans[newIndex].id);
        onPlanSelect(plans[newIndex].id);
        setActiveIndex(newIndex);
      }
    }
  };

  const renderItem = ({ item, index }: { item: Omit<PlanProps, 'onSelect'>, index: number }) => {
    return (
      <View
        style={[
          styles.planCardWrapper,
          layout === 'phone' && {
            width: ITEM_WIDTH,
            marginHorizontal: ITEM_SPACING / 2,
          },
          layout !== 'phone' && styles.planCardWrapperDesktop
        ]}
      >
        <PlanCard
          {...item}
          isSelected={selected === item.id}
          onSelect={handleSelect}
          onRegistration={onRegistration}
          buttonText={selected === item.id ? 'Selected' : 'Select Plan'}
        />
      </View>
    );
  };

  const getItemLayout = (data: any, index: number) => ({
    length: ITEM_WIDTH,
    offset: ITEM_OFFSET * index,
    index,
  });

  if (isLoading) {
    return (
      <View style={styles.loadingContainer}>
        <Text>Loading plans...</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {currentPlan && <View style={{ flexDirection: 'row', marginBottom: 12 }}>
        <ODSText type='h6-light' color={colors.text.primary}>Your current plan: </ODSText>
        <ODSText type='h6' color={colors.text.primary}>{currentPlan}</ODSText>
      </View>}

      {title && <ODSText type='h4' color={colors.text.primary} marginBottom={16}>{title}</ODSText>}

      {(false) ? (
        <FlatList
          ref={flatListRef}
          data={plans || []}
          renderItem={renderItem}
          keyExtractor={(item) => item.id}
          horizontal
          showsHorizontalScrollIndicator={false}
          snapToInterval={ITEM_OFFSET}
          snapToAlignment="center"
          decelerationRate="fast"
          contentContainerStyle={{
            paddingHorizontal: FIRST_ITEM_INSET,
            paddingVertical: 8
          }}
          onScroll={handleScroll}
          onMomentumScrollEnd={onMomentumScrollEnd}
          scrollEventThrottle={16}
          getItemLayout={getItemLayout}
          initialScrollIndex={initialIndex > -1 ? initialIndex : 0}
          onScrollToIndexFailed={(info) => {
            const wait = new Promise(resolve => setTimeout(resolve, 100));
            wait.then(() => {
              try {
                flatListRef.current?.scrollToOffset({
                  offset: info.index * ITEM_OFFSET,
                  animated: false
                });
              } catch (error) {
                console.log('Error in onScrollToIndexFailed:', error);
              }
            });
          }}
          removeClippedSubviews={false}
          maxToRenderPerBatch={3}
          windowSize={5}
        />
      ) : (
        <ScrollView
          ref={scrollViewRef}
          contentContainerStyle={[
            styles.plansContainer,
            layout !== "phone" && styles.plansContainerDesktop
          ]}
          showsVerticalScrollIndicator={false}
          horizontal={layout === "phone" ? false : true}
          showsHorizontalScrollIndicator={false}
        >
          {(plans || []).map((plan, index) => renderItem({ item: plan, index }))}
        </ScrollView>
      )}

      {(false) && plans.length > 1 && (
        <View style={styles.paginationContainer}>
          {plans.map((_, index) => (
            <View
              key={index}
              style={[
                styles.paginationDot,
                { backgroundColor: index === activeIndex ? colors.theme.purple : '#D9D9D9' }
              ]}
            />
          ))}
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    paddingVertical: 24,
    alignItems: 'center',
    width: '100%',
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 24,
    textAlign: 'center',
  },
  plansContainer: {
    paddingBottom: 16,
    alignItems: 'center',
  },
  plansContainerDesktop: {
    flexDirection: 'row',
    justifyContent: 'center',
    paddingHorizontal: 16,
  },
  planCardWrapper: {
    width: '100%',
    marginBottom: 16,
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 3.84,
    elevation: 5,
  },
  planCardWrapperDesktop: {
    width: 'auto',
    marginHorizontal: 16,
    minWidth: 300,
    maxWidth: 350,
  },
  loadingContainer: {
    padding: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  paginationContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 24,
    marginBottom: 8,
  },
  paginationDot: {
    width: 24,
    height: 4,
    borderRadius: 4,
    marginHorizontal: 6,
  },
});

export default ODSPlanSelector;